import React, { useMemo } from 'react'
import {
  AsyncCampaignCard,
  AsyncSourceCard,
  AsyncStatsCard,
  Button,
  CampaignSuggestion,
  SourceSuggestion,
} from '@demandlocal/components'
import InfoMessage from '../../../../../components/InfoMessage'
import { Outlet, useNavigate, useParams } from 'react-router'
import { useGetDMSConnection } from '../../../../../api/dealership/profile/useGetDMSConnection'
import { useGetCRMConnection } from '../../../../../api/dealership/profile/useGetCRMConnection'
import { useGetInventoryTotals } from '../../../../../api/dealership/profile/useGetInventoryTotals'
import { useGetTotalCustomers } from '../../../../../api/dealership/profile/useGetTotalCustomers'
import { useGetPhoneCalls } from '../../../../../api/dealership/profile/useGetPhoneCalls'
import { useGetDealerProfileSources } from '../../../../../api/dealership/profile/useGetDealerProfileSources'
import { useGetProfileMetrics } from '../../../../../api/dealership/profile/useGetProfileMetrics'
import { useGetSiteVisits } from '../../../../../api/dealership/profile/useGetSiteVisits'
import { ClickLineChart } from '../../../../../components/Charts/Reports/ClickLineChart'
import { productCodeToVendorIcon } from '../../../../../lib/utils/mapVendorsToLogos'
import { formatNumber, formatPercentage } from '../../../../../lib/utils/formatNumber'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import { useGetProfileBudgetBreakdown } from '../../../../../api/dealership/profile/useGetProfileBudgetBreakdown'
import PolarAreaChart from '../../../../../components/Charts/PolarAreaChart'
import dayjs from 'dayjs'
import { useGetAllCampaignsByDealership } from '../../../../../api/dealership/campaigns/useGetAllCampaignsByDealership'

const loadingCampaign = {
  client_campaign_id: 0,
  client_campaign_display_name: '',
  vendor_id: 0,
  product_code: '',
  product_name: '',
  start_date: '',
  end_date: '',
  clicks: 0,
  impressions: 0,
  views: 0,
  audience_impressions: 0,
  vehicles: 0,
  ctr: 0,
  vtr: 0,
  leads: 0,
  calls: 0,
  demand_local_net: 0,
  budget: 0,
}

const loadingSource = {
  customer_source: '',
  update_date: '',
  total_customers: 0,
  id: 0,
}

export default function PerformanceProfile(): JSX.Element {
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')

  const { agency_id } = useParams()
  const agency = useGetAgencyByAgencyId(agency_id)

  const { data: connectedDMS = { data: 0 }, isLoading: isLoadingConnectedDMS } =
    useGetDMSConnection()

  const { data: connectedCRM = { data: 0 }, isLoading: isLoadingConnectedCRM } =
    useGetCRMConnection()

  const { data: totalCustomers = { data: 0 }, isLoading: isLoadingTotalCustomers } =
    useGetTotalCustomers()

  const { data: siteVisits = { data: 0 }, isLoading: isLoadingSiteVisits } =
    useGetSiteVisits()

  const { data: phoneCalls = { data: 0 }, isLoading: isLoadingPhoneCalls } =
    useGetPhoneCalls({})

  const {
    data: inventoryTotals = { total: 0, used: 0, new: 0 },
    isLoading: isLoadingInventoryTotals,
  } = useGetInventoryTotals()

  const {
    data: campaignsList = { data: [loadingCampaign, loadingCampaign, loadingCampaign] },
    isLoading: isLoadingCampaigns,
  } = useGetAllCampaignsByDealership({
    filters: {
      startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
  })

  const {
    data: sourcesList = { sources: [loadingSource, loadingSource, loadingSource] },
    isLoading: isLoadingSources,
  } = useGetDealerProfileSources()

  const { data: metricsData = { data: [] }, isLoading: isLoadingMetrics } =
    useGetProfileMetrics()

  const { data: budgetData = { data: [] }, isLoading: isLoadingBudgetData } =
    useGetProfileBudgetBreakdown()

  const hasNoMetrics = useMemo(() => {
    if (!isLoadingMetrics)
      return metricsData?.data.reduce((acc, cur) => cur.impressions + acc, 0) === 0
    else return false
  }, [isLoadingMetrics, metricsData])

  return (
    <div className="flex flex-col gap-5 w-full font-Spartan px-10">
      <div className="grid grid-cols-3 gap-5">
        <AsyncStatsCard
          color="bg-secondary-malachite-green/10"
          title={'Active Campaigns'}
          content={
            <span className="font-bold">{Number(campaignsList?.data?.length || 0)}</span>
          }
          isLoading={isLoadingCampaigns}
        />
        {isLoadingConnectedDMS || connectedDMS?.data > 0 ? (
          <AsyncStatsCard
            title={'DMS Customers'}
            content={<span className="font-bold">{connectedDMS?.data}</span>}
            isLoading={isLoadingConnectedDMS}
          />
        ) : (
          <div
            className="border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-center items-center cursor-pointer"
            onClick={() =>
              navigate(
                `${t('dealership.customers.sources.create', { dealership })}/sales`,
              )
            }>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Connect your DMS
            </span>
          </div>
        )}
        {isLoadingConnectedCRM || connectedCRM?.data > 0 ? (
          <AsyncStatsCard
            title={'CRM Customers'}
            content={<span className="font-bold">{connectedCRM?.data}</span>}
            isLoading={isLoadingConnectedCRM}
          />
        ) : (
          <div
            className="border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-center items-center cursor-pointer"
            onClick={() =>
              navigate(
                `${t('dealership.customers.sources.create', { dealership })}/leads`,
              )
            }>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Connect your CRM
            </span>
          </div>
        )}
      </div>
      {hasNoMetrics ? (
        <InfoMessage
          content={
            <div className="flex flex-row justify-between w-full p-5">
              <div className="flex flex-col gap-1">
                <span className="font-bold text-xl">
                  Don't want to do it yourself? Let us market your audience
                </span>
                <span>
                  Grow your audience, market your cars, create highly effective targeted
                  ads.
                </span>
              </div>
              <Button
                variant="rainbow"
                onClick={() =>
                  navigate(t('dealership.campaigns.create', { dealership }))
                }>
                Create Campaign
              </Button>
            </div>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Budget Allocation (%)</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={
                  budgetData?.data.map((data) => ({
                    label: data.medium,
                    value: data.budget,
                  })) ?? []
                }
                customOptions={{
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `${
                          (context.label as { label: string }).label
                        }: ${formatPercentage(
                          (context.label as { value: number }).value / 100,
                        )}`
                      },
                    },
                    displayColors: false,
                  },
                }}
                chartScale="linear"
                isLoading={isLoadingBudgetData}
              />
            </div>
          </div>
          <ClickLineChart
            data={metricsData?.data}
            isLoading={isLoadingMetrics}
            sheetTitle={'Clicks'}
            isPrevious={false}
          />
        </div>
      )}
      <div className="grid grid-cols-3 gap-5">
        {isLoadingTotalCustomers || totalCustomers?.data > 0 ? (
          <AsyncStatsCard
            title={'Total Customers'}
            content={
              <span className="font-bold">{formatNumber(totalCustomers?.data)}</span>
            }
            isLoading={isLoadingTotalCustomers}
          />
        ) : (
          <div
            className="flex flex-row border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-between items-center cursor-pointer p-3"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            <div className="flex flex-col gap-3 opacity-50">
              <span className="text-xs text-primary-xiketic">Total Customers</span>
              <span className="font-bold text-primary-xiketic">
                {formatNumber(totalCustomers?.data)}
              </span>
            </div>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Add Your Customers
            </span>
          </div>
        )}
        <AsyncStatsCard
          title={'Attributed Unique Site Visitors'}
          content={<span className="font-bold">{formatNumber(siteVisits?.data)}</span>}
          isLoading={isLoadingSiteVisits}
        />
        <AsyncStatsCard
          title={'Phone calls'}
          content={<span className="font-bold">{formatNumber(phoneCalls?.data)}</span>}
          isLoading={isLoadingPhoneCalls}
        />
        {isLoadingInventoryTotals || inventoryTotals?.total > 0 ? (
          <AsyncStatsCard
            title={'Cars in Inventory'}
            content={
              <span className="font-bold">{formatNumber(inventoryTotals?.total)}</span>
            }
            isLoading={isLoadingInventoryTotals}
          />
        ) : (
          <div
            className="flex flex-row border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-between items-center cursor-pointer p-3"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            <div className="flex flex-col gap-3 opacity-50">
              <span className="text-xs text-primary-xiketic">Cars in Inventory</span>
              <span className="font-bold text-primary-xiketic">
                {formatNumber(inventoryTotals?.total)}
              </span>
            </div>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Add Your Inventory
            </span>
          </div>
        )}
        <AsyncStatsCard
          title={'New Cars in Inventory'}
          content={
            <span className="font-bold">{formatNumber(inventoryTotals?.used)}</span>
          }
          isLoading={isLoadingInventoryTotals}
        />
        <AsyncStatsCard
          title={'Used Cars in Inventory'}
          content={
            <span className="font-bold">{formatNumber(inventoryTotals?.new)}</span>
          }
          isLoading={isLoadingInventoryTotals}
        />
      </div>
      <div className="flex w-full items-center justify-center">
        {!isLoadingMetrics && !hasNoMetrics && (
          <InfoMessage
            content={
              <div className="flex flex-row justify-between w-full p-5">
                <div className="flex flex-col gap-1">
                  <span className="font-bold text-xl">
                    Connect your {!!connectedCRM?.data ? 'DMS' : 'CRM'} to see more in
                    depth reporting
                  </span>
                  <span>
                    Market your cars, create highly effective targeted ads and see sales.
                  </span>
                </div>
                <Button
                  variant="rainbow"
                  onClick={() =>
                    navigate(
                      `${t('dealership.customers.sources.create', { dealership })}/${
                        !!connectedCRM?.data ? 'sales' : 'leads'
                      }`,
                    )
                  }>
                  Connect your {!!connectedCRM?.data ? 'DMS' : 'CRM'}
                </Button>
              </div>
            }
          />
        )}
      </div>
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items center">
          <span className="font-bold">Campaigns</span>
          <Button
            variant="secondary"
            onClick={() => navigate(t('dealership.campaigns.list', { dealership }))}>
            See Campaign Level Reporting
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {campaignsList?.data.slice(0, 3).map((campaign, idx) => (
            <AsyncCampaignCard
              key={idx}
              campaign={campaign}
              isLoading={isLoadingCampaigns}
              vendor_icon={
                <div className="flex max-w-[40px] object-contain rounded-full">
                  {productCodeToVendorIcon(campaign?.product_code)}
                </div>
              }
              to={t('dealership.campaigns.report', { dealership, campaign })}
              show_budget_type={agency.data?.show_budget_type}
            />
          ))}
          {!isLoadingCampaigns &&
            Array.from({ length: 3 - Number(campaignsList?.data?.length || 0) }).map(
              (_, idx) => (
                <CampaignSuggestion
                  key={`suggestion_${idx}`}
                  index={idx}
                  to={t('dealership.campaigns.create', { dealership })}
                />
              ),
            )}
        </div>
      </div>
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items center">
          <span className="font-bold">Sources</span>
          <Button
            animate={false}
            variant="secondary"
            onClick={() => navigate('sources/new')}>
            Add more sources
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {sourcesList.sources?.slice(0, 3).map((source, idx) => (
            <div key={idx}>
              <AsyncSourceCard
                title={source.customer_source}
                last_updated={source.updated_date}
                total_customers={source.total_customers}
                id={source.id}
                isLoading={isLoadingSources}
                to={t('dealership.customers.sources.report', { dealership, source })}
              />
            </div>
          ))}
          {!isLoadingSources &&
            Array.from({ length: 3 - sourcesList?.sources?.length }).map((_, idx) => (
              <SourceSuggestion
                key={`suggestions${idx}`}
                index={idx}
                to={t('dealership.customers.sources.create', { dealership })}
              />
            ))}
        </div>
      </div>
      <Outlet />
    </div>
  )
}
